import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import ScreamSkeleton from '../../util/ScreamSkeleton';

import { connect } from 'react-redux';
import { getScreams } from '../../redux/actions/dataActions';
import Board from '../../components/ArchivedChess/Board';
import { Chess } from 'chess.js';
import Controls from '../../components/ArchivedChess/Controls';
import Moves from '../../components/ArchivedChess/Moves';
import useMoveIndex from '../../hooks/useMoveIndex';
import { postGame } from '../../util/api/game';
import {
  getFlippedBoardOrientation,
  getMove,
  getMoveAndVariationIndices,
  getTurnInFen,
  isMoveInVariation,
  isVariationMove,
  onNextMove,
  onPrevMove,
  getPgnFromMoves,
  getCurrentBoardPosition,
  getCurrentVariation,
  getParents,
  resetPositionCache
} from '../../util/Chess';
import Select from 'react-select';
import constants, { TAGS, DIFFICULTY, allowedEcoGroups } from '../../util/constants';
import { ChessSocialContext } from '../../util/ChessSocialContext';
import ChessAnalysisWrapper from '../../util/ChessAnalysisWrapper';
import FAQAndSettingsModal from '../../components/modals/FAQAndSettingsModal';
import FENBuilderModal from '../../components/modals/FENBuilderModal';
import DraftsModal from '../../components/modals/DraftsModal';
import GameModeChooserModal from '../../components/modals/GameModeChooserModal';
import StartPositionModal from '../../components/modals/StartPositionModal';
import DeleteConfirmModal from '../../components/modals/DeleteConfirmModal';
import Stockfish from '../Stockfish';
import StockfishEval from '../../components/ArchivedChess/StockfishEval';
import { collection, query, where, getDocs, doc, addDoc, setDoc } from "firebase/firestore";
import { firestore } from '../../firebase';

import { COLORS } from '../../constants/colors';


import BrilliantGray from '../../images/FooterIcons/Brilliance2.png';
import BrilliantBlue from '../../images/FooterIcons/Brilliance4.png';

const VISIBILITIES = constants.VISIBILITIES;
const CHESSBOARD = constants.CHESSBOARD;

// Create custom styles for the component
const customStyles = {
  placeholderRed: {
    '::placeholder': {
      color: '#ff0050'
    }
  }
};

// Add custom CSS to document once
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `
  .placeholder-red::placeholder {
    color: #ff0050 !important;
  }
  select {
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white' width='18px' height='18px'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 20px center;
    padding-right: 40px !important;
  }
`;
document.head.appendChild(styleSheet);

const AddGame = (props) => {
  const containerRef = useRef(null);
  const [boardSize, setBoardSize] = useState(500);
  const [openModalType, setOpenModalType] = useState(''); // Ensure this is at top level

  // +++ Get setIsPuzzleMode from context +++
  const { setIsPuzzleMode } = useContext(ChessSocialContext);

  // State and refs for the chess control panel
  const [showControlPanel, setShowControlPanel] = useState(false);
  const boardIconRef = useRef(null);
  const controlPanelRef = useRef(null);

  // Initialize state from localStorage or default values
  const [localShowStockfish, setLocalShowStockfish] = useState(() => {
    // Try the 'showStockfish' key first (string format 'true'/'false')
    const simpleValue = localStorage.getItem('showStockfish');
    if (simpleValue !== null) {
      return simpleValue === 'true';
    }

    // Try the 'addGame_showStockfish' key next (JSON format)
    const jsonValue = localStorage.getItem('addGame_showStockfish');
    if (jsonValue !== null) {
      try {
        return JSON.parse(jsonValue);
      } catch (e) {
        console.error("Error parsing JSON from localStorage:", e);
      }
    }

    // Default to true if nothing found
    return true;
  });

  const [localShowStockfishEval, setLocalShowStockfishEval] = useState(() => {
    const savedValue = localStorage.getItem('addGame_showStockfishEval');
    return savedValue !== null ? JSON.parse(savedValue) : true;
  });

  const [localShowStockfishIcons, setLocalShowStockfishIcons] = useState(() => {
    // Use the same localStorage key as used in the toggle handler
    const savedValue = localStorage.getItem('showStockfishIcons');
    return savedValue === null ? false : savedValue === 'true';
  });

  // When values change, save to localStorage
  useEffect(() => {
    // Save to both keys for backward compatibility
    localStorage.setItem('showStockfish', localShowStockfish);
    localStorage.setItem('addGame_showStockfish', JSON.stringify(localShowStockfish));
  }, [localShowStockfish]);

  useEffect(() => {
    localStorage.setItem('addGame_showStockfishEval', JSON.stringify(localShowStockfishEval));
  }, [localShowStockfishEval]);

  useEffect(() => {
    // Use the same localStorage key as in the toggle handler 
    localStorage.setItem('showStockfishIcons', localShowStockfishIcons);
  }, [localShowStockfishIcons]);

  // Close the control panel when clicking outside
  useEffect(() => {
    if (showControlPanel) {
      const handleClickOutside = (event) => {
        if (
          controlPanelRef.current &&
          !controlPanelRef.current.contains(event.target) &&
          boardIconRef.current &&
          !boardIconRef.current.contains(event.target)
        ) {
          setShowControlPanel(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showControlPanel]);

  // Function to determine the robot icon color based on active features
  const getRobotColor = () => {
    return (localShowStockfish || localShowStockfishEval || localShowStockfishIcons || showArrows) ? 'rgb(76, 175, 80)' : '#ff0050';
  };

  // Add handler functions for the control panel buttons
  const handleStockfishToggle = () => {
    // Toggle the local state
    const newValue = !localShowStockfish;
    setLocalShowStockfish(newValue);

    // Synchronize with context state
    setShowStockfish(newValue);

    // Save to localStorage using a consistent key
    localStorage.setItem('showStockfish', newValue);
    localStorage.setItem('addGame_showStockfish', JSON.stringify(newValue));


  };

  const handleStockfishEvalToggle = () => {
    setLocalShowStockfishEval(!localShowStockfishEval);
  };

  const handleStockfishIconsToggle = () => {
    // Save to both local state and context
    const newValue = !localShowStockfishIcons;
    setLocalShowStockfishIcons(newValue);

    // Update context state which will affect the Board component
    setShowStockfishIcons(newValue);

    // Save to localStorage using both keys for consistency
    localStorage.setItem('showStockfishIcons', newValue);
    localStorage.setItem('addGame_showStockfishIcons', JSON.stringify(newValue));


  };

  // Move the useEffect here
  useEffect(() => {
    const flipButtonStyleSheet = document.createElement('style');
    flipButtonStyleSheet.type = 'text/css';
    flipButtonStyleSheet.innerText = `
      .flip-button-white-oriented {
        background: gold !important;
        border: 3px solid gold !important;
        color: black !important;
        transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
      }
      .flip-button-white-oriented:hover {
        background: black !important;
        border-color: black !important;
        color: gold !important;
      }
      .flip-button-black-oriented {
        background: gold !important;
        border: 3px solid gold !important;
        color: white !important;
        transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
      }
      .flip-button-black-oriented:hover {
        background: white !important;
        border-color: white !important;
        color: gold !important;
      }
    `;
    document.head.appendChild(flipButtonStyleSheet);

    // Cleanup function to remove the style sheet when the component unmounts
    return () => {
      document.head.removeChild(flipButtonStyleSheet);
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  // Use the global context instead of local state
  const {
    showEmoji,
    setShowEmoji,
    showStockfishIcons,
    setShowStockfishIcons,
    showArrows,
    setShowArrows,
    evaluation,
    showStockfish,
    setShowStockfish
  } = useContext(ChessSocialContext);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const viewportWidth = window.innerWidth;
        const availableWidth = viewportWidth - 875;

        // Now container and board will be same size
        const containerSize = Math.min(availableWidth, 900);
        const boardSize = Math.max(containerSize, 500);

        setBoardSize(boardSize);
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const { setActiveTab, activeTab } = useContext(ChessSocialContext);

  // Add new state variables to track analysis data
  const [game, setGame] = useState(new Chess());
  const [moves, setMoves] = useState([]);
  const [gameMetadata, setGameMetadata] = useState({
    visibility: 'Public',
    isPrivate: false
  });

  // Track if we should use cached analysis for the current position
  const [hasAnalysisCache, setHasAnalysisCache] = useState(false);
  const [currentAnalysisCache, setCurrentAnalysisCache] = useState(null);

  const [step, setStep] = useState(0);
  // State to control the visibility of the controls section
  const [showControls, setShowControls] = useState(false);
  // Add state to track if annotation textarea is focused
  const [isAnnotationFocused, setIsAnnotationFocused] = useState(false);
  // Previous moves count for comparing when moves have changed
  const prevMovesCountRef = useRef(0);

  // Add an effect to log when annotation focus changes
  useEffect(() => {
  }, [isAnnotationFocused]);

  // Add debugging for document-level events to help identify any issues
  useEffect(() => {
    const documentKeyListener = (e) => {
      if (isAnnotationFocused && ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {

      }
    };

    document.addEventListener('keydown', documentKeyListener);
    return () => {
      document.removeEventListener('keydown', documentKeyListener);
    };
  }, [isAnnotationFocused]);

  // Effect to close the control panel when a new move is made
  useEffect(() => {
    // Skip the initial render
    if (moves.length > 0 && prevMovesCountRef.current !== moves.length) {
      // Close the control panel when a move is made
      setShowControls(false);
    }
    // Update the previous moves count
    prevMovesCountRef.current = moves.length;
  }, [moves]);

  const [gameCaption, setGameCaption] = useState('');
  const [moveContent, setMoveContent] = useState('');
  const [variationContent, setVariationContent] = useState('');
  const [tags, setTags] = useState([]);
  // Add state for tag selection error indication
  const [showTagError, setShowTagError] = useState(false);
  
  // New state variables for opening selection
  const [isOpeningSelectionMode, setIsOpeningSelectionMode] = useState(false);
  const [openingOptions, setOpeningOptions] = useState([]);
  const [isLoadingOpenings, setIsLoadingOpenings] = useState(false);
  const [selectedOpening, setSelectedOpening] = useState(null);
  
  const [rootFen, setRootFen] = useState(CHESSBOARD.START_POSITION);
  const [pgn, setPgn] = useState('');
  const [starredMoves, setStarredMoves] = useState([]);
  
  const [orientation, setOrientation] = useState('white');
  const [boardStartingTurn, setBoardStartingTurn] = useState('w'); // Add state for tracking the FEN's starting turn

  const { screams, loading } = props.data;

  // Near the top of the component where the useMoveIndex hook is used:
  const [skipMoveIndexUpdate, setSkipMoveIndexUpdate] = useState(false);

  const [currentMoveIdx, setCurrentMoveIdx] = useMoveIndex({
    moves,
    gameMetadata,
    setGame,
    setMoveContent,
    setVariationContent,
    skipUpdate: skipMoveIndexUpdate
  });

  // Add effect to sync variationContent with current move's variation caption
  useEffect(() => {
    if (isMoveInVariation(currentMoveIdx, moves)) {
      const [moveRef, variationIdx] = getMoveAndVariationIndices(currentMoveIdx, moves);
      if (moveRef?.variationCaptions?.[variationIdx]) {
        setVariationContent(moveRef.variationCaptions[variationIdx]);
      } else {
        setVariationContent('');
      }
    } else {
      setVariationContent('');
    }
  }, [currentMoveIdx, moves]);

  // Create a function to save analysis data for the current move
  const saveAnalysisToMove = (analysisData) => {
    if (!analysisData || currentMoveIdx === '-1') return;

    // Clone the current moves array
    const updatedMoves = JSON.parse(JSON.stringify(moves));
    const moveIndex = parseInt(currentMoveIdx);

    // Update the current move with analysis data
    if (updatedMoves[moveIndex]) {
      updatedMoves[moveIndex] = {
        ...updatedMoves[moveIndex],
        analysis: {
          pvs: analysisData.pvs || [],             // Principal variations (lines)
          scores: analysisData.scores || [],       // Scores for each line
          classification: analysisData.classification || null,  // Move classification
          depth: analysisData.depth || null        // Add depth to record how deep the analysis went
        }
      };

      // Update the moves state
      setMoves(updatedMoves);

    }
  };

  // Add a function to check for cached analysis when a move is selected
  const checkForCachedAnalysis = (moveIndex) => {


    if (moveIndex === '-1') {
      // No analysis for starting position
      setHasAnalysisCache(false);
      setCurrentAnalysisCache(null);
      return;
    }

    const moveIdx = parseInt(moveIndex);
    const move = moves[moveIdx];

    if (move && move.analysis) {


      // We have cached analysis for this move
      setHasAnalysisCache(true);
      setCurrentAnalysisCache(move.analysis);

      return true;
    } else {
      // No cached analysis found
      setHasAnalysisCache(false);
      setCurrentAnalysisCache(null);
      return false;
    }
  };

  // Create a function to receive analysis data from Stockfish.js using useCallback
  const receiveStockfishAnalysis = useCallback((analysisData) => {
    // Only save complete analysis data that has reached target depth
    if (analysisData && analysisData.isComplete) {
      // Save the current variation content
      const currentVariationContent = variationContent;
      
      // Update the moves with analysis data
      saveAnalysisToMove(analysisData);
      
      // Restore the variation content after the update
      setVariationContent(currentVariationContent);
    }
  }, [saveAnalysisToMove, variationContent]); // Add variationContent to dependencies

  // Add an effect to expose the receiveStockfishAnalysis function to the window
  useEffect(() => {
    // Expose the function to the window object for Stockfish.js to call
    window.receiveStockfishAnalysis = receiveStockfishAnalysis;

    // Also expose the currentAnalysisCache for Stockfish.js to access
    window.currentAnalysisCache = currentAnalysisCache;

    // Clean up when component unmounts
    return () => {
      window.receiveStockfishAnalysis = undefined;
      window.currentAnalysisCache = undefined;
    };
  }, [receiveStockfishAnalysis, currentAnalysisCache]);

  // Update moveNavigation to check for cached analysis when navigating
  const moveNavigation = (targetIndex, shouldUpdateContent = true) => {

// Update content for current move before changing
// if (shouldUpdateContent) {
//   updateContentForMove(moveContent);
// }

// // Get the target index as a number
// const targetIdx = parseInt(targetIndex);

// // Set the current move index
// setCurrentMoveIdx(targetIndex);

// // Check for cached analysis for the target move
// checkForCachedAnalysis(targetIndex);

// // Apply moves up to the target index
// const chessInstance = new Chess(rootFen);


// // Apply moves up to the target index
// for (let i = 0; i <= targetIdx; i++) {
//   if (i < moves.length) {
//     try {
//       const result = chessInstance.move(moves[i].notation);
//       if (!result) {
//         console.error(`Navigation - Failed to apply move ${i}: ${moves[i].notation}`);
//         break;
//       }
//     } catch (err) {
//       console.error(`Navigation - Error applying move ${i}: ${moves[i].notation}`, err);
//       break;
    
    // Get the current position - don't apply it yet, let the Board component handle it
    // by using currentMoveIdx which was already updated in handleMoveClick
    
    // Handle variations properly by setting direct flag for instant rendering
    const isVariationMove = targetIndex.includes('|');
    if (isVariationMove) {
        try {
            // Set global flag for direct variation transition if not already set
            // This helps ensure the position renders immediately
            if (!window.chessDirectVariationMove || 
                window.chessDirectVariationMove.moveNotation !== targetIndex) {
              
                window.chessDirectVariationMove = {
                    moveNotation: targetIndex,
                    timestamp: Date.now()
                };
            }
        } catch (e) {
            console.error('Error setting variation flags:', e);
        }
    }
    
    // Update content for current move if needed
    if (shouldUpdateContent && moveContent) {
        updateContentForMove(moveContent);
    }
    
    // Clear any temporary annotation state
    setIsAnnotationFocused(false);
    
    // NOTE: We don't need to call setCurrentMoveIdx here as it's already
    // been set in handleMoveClick before this is called
};

  // Update keyDownHandler to use our custom navigation function
  const keyDownHandler = useCallback(
    (e) => {
      // Immediately return if the annotation textarea is focused
      if (isAnnotationFocused) {
        // This means the arrow/space key events should be handled by the textarea itself
        return;
      }

      if (e.key === 'ArrowLeft') {
        // updateContentForMove(moveContent);
        // onPrevMove(currentMoveIdx, setCurrentMoveIdx);
        // Use our custom navigation instead of onPrevMove
        const prevIdx = parseInt(currentMoveIdx) - 1;
        if (prevIdx >= -1) {
          moveNavigation(prevIdx.toString());
        }
      } else if (e.key === 'ArrowRight') {
        // updateContentForMove(moveContent);
        // onNextMove(currentMoveIdx, setCurrentMoveIdx, moves);
        // Use our custom navigation instead of onNextMove
        // const nextIdx = parseInt(currentMoveIdx) + 1;
        // if (nextIdx < moves.length) {
        //   moveNavigation(nextIdx.toString());
        // }
      }
    },
    //     [currentMoveIdx, moves, setCurrentMoveIdx, moveContent, isAnnotationFocused],
    [currentMoveIdx, moves, moveContent, isAnnotationFocused, rootFen],
  );

  // Fix the currentMoveIdx state to prevent the original useMoveIndex effect from running
  const handleMoveClick = (idx, shouldUpdateContent = true) => {
    // Check if this would be a variation move
    const isVariationMove = idx.includes('|');
    
    // If this is a variation move, set the direct variation flag
    if (isVariationMove) {
      try {
        // Set global flag for direct variation transition
        window.chessDirectVariationMove = {
          moveNotation: idx,
          timestamp: Date.now()
        };
        
        // Preload the position if possible
        if (window.preloadVariationPosition) {
          window.preloadVariationPosition(idx);
        }
      } catch (e) {
        console.error('Error setting variation flags:', e);
      }
    }

    // Log any movement in currentMoveIdx
    if (currentMoveIdx !== idx) {
      // Check for cached analysis before switching moves
      checkForCachedAnalysis(idx);

      setCurrentMoveIdx(idx);
      moveNavigation(idx, shouldUpdateContent);
    }
  };

  useEffect(() => {
    document.removeEventListener('keydown', keyDownHandler);
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [keyDownHandler]);

  const updateContentForMove = (newContent) => {
    const move = getMove(moves, currentMoveIdx);
    if (!move) {
      return;
    }
    // move.content = newContent;
    // Create a new moves array with the updated content
    const updatedMoves = moves.map((m, idx) => {
      if (idx.toString() === currentMoveIdx) {
        return { ...m, content: newContent };
      }
      return m;
    });
    
    // Update the moves array
    setMoves(updatedMoves);
  };

  // Add effect to sync moveContent with current move's content
  useEffect(() => {
    const move = getMove(moves, currentMoveIdx);
    if (move) {
      setMoveContent(move.content || '');
    } else {
      setMoveContent('');
    }
  }, [currentMoveIdx, moves]);

  // Add state for bookmark functionality
  const [userFolders, setUserFolders] = useState([]);
  const [selectedBookmarkFolder, setSelectedBookmarkFolder] = useState('NONE');
  const [isCreatingNewFolder, setIsCreatingNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderPrivate, setNewFolderPrivate] = useState(false);
  const [bookmarkNotification, setBookmarkNotification] = useState(null);

  const [selectedDifficulty, setSelectedDifficulty] = useState(() => {
    // Get saved difficulty from localStorage or default to "All Levels"
    return localStorage.getItem('preferredDifficulty') || 'All Levels';
  });

  // Function to fetch user's bookmark folders
  const fetchUserFolders = useCallback(async () => {
    if (!props.user || !props.user.credentials || !props.user.credentials.userId) {
      // console.error('User not logged in or user ID not available');
      return [];
    }

    try {
      const userId = props.user.credentials.userId;

      // Fetch bookmark folders for the current user
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", userId)
      );

      const folderSnapshot = await getDocs(folderQuery);

      let fetchedFolders = [];

      if (!folderSnapshot.empty) {
        // Process folder documents
        fetchedFolders = folderSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().folderName,
          isPrivate: doc.data().isPrivate,
          games: doc.data().games || [],
          folderIndex: doc.data().folderIndex !== undefined ? doc.data().folderIndex : 999
        }));
      } else {

      }

      // Ensure default folders exist
      let mergedFolders = [...fetchedFolders];

      // Check if Featured folder exists, if not add it
      if (!fetchedFolders.some(folder => folder.name === 'Featured')) {
        mergedFolders.push({
          name: 'Featured',
          isPrivate: false,
          games: [],
          folderIndex: 0
        });
      }

      // Check if Safe folder exists, if not add it
      if (!fetchedFolders.some(folder => folder.name === 'Safe')) {
        mergedFolders.push({
          name: 'Safe',
          isPrivate: true,
          games: [],
          folderIndex: 0
        });
      }

      // Sort folders
      mergedFolders.sort((a, b) => {
        // First sort by privacy
        if (a.isPrivate !== b.isPrivate) {
          return a.isPrivate ? 1 : -1;
        }
        // Then sort by folderIndex
        return (a.folderIndex || 999) - (b.folderIndex || 999);
      });

      setUserFolders(mergedFolders);
      return mergedFolders;
    } catch (error) {
      console.error("Error fetching user folders:", error);
      return [];
    }
  }, [props.user]);

  // Fetch folders when component mounts or user changes
  useEffect(() => {
    fetchUserFolders();
  }, [fetchUserFolders]);

  // Show bookmark notification
  const showBookmarkNotification = (message, type = 'success') => {
    setBookmarkNotification({
      message,
      type
    });

    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      setBookmarkNotification(null);
    }, 3000);
  };

  // Handle saving game to bookmark folder
  const saveGameToFolder = async (gameId, folderName) => {
    if (folderName === 'NONE') {
      return; // Don't save to any folder
    }

    try {
      // Check if user is logged in
      if (!props.user || !props.user.credentials || !props.user.credentials.userId) {
        // console.error('User not logged in or user ID not available');
        showBookmarkNotification('You must be logged in to bookmark games', 'error');
        return false;
      }

      const userId = props.user.credentials.userId;

      // Find the folder to get its privacy setting
      const folder = userFolders.find(f => f.name === folderName);
      if (!folder) {
        console.error(`Folder "${folderName}" not found`);
        showBookmarkNotification(`Folder "${folderName}" not found`, 'error');
        return false;
      }


      // Check if the folder document already exists in Firestore
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", userId),
        where("folderName", "==", folderName)
      );

      const folderSnapshot = await getDocs(folderQuery);

      if (folderSnapshot.empty) {
        // Folder doesn't exist yet, create a new one with the game at index 0
        const newFolderData = {
          userId: userId,
          folderName: folderName,
          isPrivate: folder.isPrivate,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          folderIndex: 0,
          games: [{
            gameId: gameId,
            index: 0,
            createdAt: new Date().toISOString()
          }]
        };



        // Add a new document to the "bookmarkFolders" collection
        const docRef = await addDoc(collection(firestore, "bookmarkFolders"), newFolderData);

        showBookmarkNotification(`Game saved to folder "${folderName}"`, 'success');
        return true;
      } else {
        // Folder exists, update it by adding the game at index 0 and reindexing others
        const folderDoc = folderSnapshot.docs[0];
        const folderData = folderDoc.data();

        // Check if the game is already in the folder
        const existingGameIndex = folderData.games ? folderData.games.findIndex(game => game.gameId === gameId) : -1;

        if (existingGameIndex !== -1) {

          // Game already exists, just update its index to 0 and reindex others
          const updatedGames = [...folderData.games];
          const existingGame = updatedGames.splice(existingGameIndex, 1)[0];

          // Increment index of all other games
          updatedGames.forEach(game => {
            game.index += 1;
          });

          // Add the existing game at index 0
          existingGame.index = 0;
          updatedGames.unshift(existingGame);

          // Update the folder document
          await setDoc(folderDoc.ref, {
            ...folderData,
            games: updatedGames,
            updatedAt: new Date().toISOString()
          });


          showBookmarkNotification(`Game updated in folder "${folderName}"`, 'success');
          return true;
        } else {
          // Game doesn't exist in the folder, add it at index 0
          const updatedGames = folderData.games || [];

          // Increment index of all existing games
          updatedGames.forEach(game => {
            game.index += 1;
          });

          // Add the new game at index 0
          updatedGames.unshift({
            gameId: gameId,
            index: 0,
            createdAt: new Date().toISOString()
          });

          // Update the folder document
          await setDoc(folderDoc.ref, {
            ...folderData,
            games: updatedGames,
            updatedAt: new Date().toISOString()
          });


          showBookmarkNotification(`Game saved to folder "${folderName}"`, 'success');
          return true;
        }
      }
    } catch (error) {
      console.error("Error saving bookmark: ", error);
      showBookmarkNotification('Failed to save bookmark. Please try again.', 'error');
      return false;
    }
  };

  // Create a new folder and add game to it
  const createNewFolderAndAddGame = async (gameId, folderName, isPrivate) => {
    try {


      // Check if user is logged in
      if (!props.user || !props.user.credentials || !props.user.credentials.userId) {
        // console.error('User not logged in or user ID not available');
        showBookmarkNotification('You must be logged in to bookmark games', 'error');
        return false;
      }

      const userId = props.user.credentials.userId;

      // Validate folder name
      if (!folderName || !folderName.trim()) {
        console.error('Folder name is empty');
        showBookmarkNotification('Please enter a folder name', 'error');
        return false;
      }

      // Check if folder with this name already exists
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", userId),
        where("folderName", "==", folderName.trim())
      );

      const folderSnapshot = await getDocs(folderQuery);

      if (!folderSnapshot.empty) {
        console.error(`Folder "${folderName.trim()}" already exists`);
        showBookmarkNotification(`Folder "${folderName.trim()}" already exists`, 'error');
        return false;
      }

      // Get the highest folderIndex in the appropriate category
      const categoryFolders = userFolders.filter(f =>
        f.isPrivate === isPrivate &&
        f.name !== 'Featured' &&
        f.name !== 'Safe'
      );

      const maxIndex = categoryFolders.length > 0
        ? Math.max(...categoryFolders.map(f => f.folderIndex || 0), 0)
        : 0;

      // Create new folder with the game at index 0
      const newFolderData = {
        userId: userId,
        folderName: folderName.trim(),
        isPrivate: isPrivate,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        folderIndex: maxIndex + 1, // Add to the end of the appropriate category
        games: [{
          gameId: gameId,
          index: 0,
          createdAt: new Date().toISOString()
        }]
      };



      // Add a new document to the "bookmarkFolders" collection
      const docRef = await addDoc(collection(firestore, "bookmarkFolders"), newFolderData);


      // Show success notification
      showBookmarkNotification(`Game saved to new folder "${folderName.trim()}"`, 'success');

      // Update folder list
      await fetchUserFolders();

      return true;
    } catch (error) {
      console.error("Error creating new folder:", error);
      showBookmarkNotification('Failed to create folder. Please try again.', 'error');
      return false;
    }
  };

  // Add a helper function to check if the current game is a Challenge

  // PREVIOUS LOGIC:
  // const isFormValid = () => {
  //   const hasTags = tags.length >= 1 && tags.length <= 2;
  //   const hasValidCaption = gameCaption.trim().length >= 2;
  //   const hasRequiredMoves = gameMetadata.gameMode !== 'Puzzle' || moves.length > 0;
  //   return hasTags && hasValidCaption && hasRequiredMoves;
  // };
  const isChallenge = () => {
    return tags.some(tag => tag.value === "Challenge");
  };

  // Add this new function to check if form is valid
  const isFormValid = () => {
    const hasTags = tags.length >= 1 && tags.length <= 2;
    const hasValidCaption = gameCaption.trim().length >= 2;

    // For Challenges, no moves required
    // For Puzzles, moves are required
    // For all other types (Lessons and defaults), moves are required
    const hasRequiredMoves = isChallenge() || moves.length > 0;

    return hasTags && hasValidCaption && hasRequiredMoves;
  };

  const onSubmitGame = async () => {
    // Don't check for moves for Challenges
    if (gameMetadata.gameMode === 'Puzzle' && moves.length === 0) {
      alert('Please add moves for Puzzle mode');
      return;
    }

    // For non-Challenges without the Challenge tag, require moves
    if (!isChallenge() && moves.length === 0) {
      alert('Please add at least one move');
      return;
    }

    try {
      // Get the user's handle from props
      const userHandle = props.user?.credentials?.handle;

      if (!userHandle) {
        console.error("User handle not found. Cannot post game without a handle.");
        alert('Could not add game: User information is missing. Please try logging in again.');
        return;
      }



      // Log the game data for debugging purposes
      const gameData = {
        caption: gameCaption,
        fen: isChallenge() || !moves.length ? rootFen : (gameMetadata.gameMode === undefined ? rootFen : game.history({ verbose: true })[0]?.before || rootFen),
        movesCount: moves.length,
        tags: tags.map(tag => tag.value),
        isChallenge: isChallenge(),
        isPrivate: gameMetadata.isPrivate,
        difficulty: selectedDifficulty
      };


      // First, handle bookmark data preparation
      let shouldCreateNewFolder = false;
      let newFolder = null;

      if (isCreatingNewFolder && newFolderName.trim()) {
        shouldCreateNewFolder = true;
        newFolder = {
          name: newFolderName.trim(),
          isPrivate: newFolderPrivate
        };

      }

      const newGame = await postGame({
        caption: gameCaption,
        fen: isChallenge() || !moves.length
          ? rootFen
          : (gameMetadata.gameMode === undefined
            ? rootFen
            : game.history({ verbose: true })[0]?.before || rootFen),
        comments: [],
        suggestedMoves: [],
        moves: moves,
        starredMoves: starredMoves,
        tags: tags.map(tag => tag.value),
        pov: orientation === 'white',
        // Add the handle as the primary creator identifier
        creatorHandle: userHandle,
        // Keep the userID as a reference for backend purposes
        creator: props.user.credentials.userId,
        // Add a flag to indicate this is a challenge game
        isChallenge: isChallenge(),
        // Add isPrivate flag for game privacy --> Still need to update VIEWER logic for this, and more.
        isPrivate: gameMetadata.isPrivate,
        difficulty: selectedDifficulty
      });

      const newGameId = newGame?.data?.id;

      if (newGameId) {
        let bookmarkSuccess = true;

        // Handle bookmark folder selection
        try {
          if (shouldCreateNewFolder) {
            // Create new folder and add game to it
            bookmarkSuccess = await createNewFolderAndAddGame(newGameId, newFolder.name, newFolder.isPrivate);
          } else if (selectedBookmarkFolder !== 'NONE') {

            // Add to existing folder
            bookmarkSuccess = await saveGameToFolder(newGameId, selectedBookmarkFolder);
          }


        } catch (bookmarkError) {
          console.error("Error handling bookmark:", bookmarkError);
          bookmarkSuccess = false;
        }

        // Add a slight delay to ensure notifications are seen before navigation
        setTimeout(() => {
          // Navigate to the new game regardless of bookmark status
          props.history.push(`/games/${newGameId}?ctx=newGame`);
        }, bookmarkSuccess ? 1000 : 0); // If bookmark successful, wait 1 second for notification
      } else {
        props.history.push('/');
      }
    } catch (err) {
      console.error("Error posting game:", err);
      // Provide a more helpful error message
      let errorMessage = 'Could not add game. ';

      // Check if there's a response with an error message
      if (err.response && err.response.data) {
        if (err.response.data.error) {
          errorMessage += err.response.data.error;
        } else if (err.response.data.err) {
          errorMessage += err.response.data.err;
        }
      } else if (err.message) {
        errorMessage += err.message;
      } else {
        errorMessage += 'Please try again.';
      }

      alert(errorMessage);
    }
  };

  const resetPost = () => {
    setGameCaption('');
    setPgn('');
    setMoves([]);
    setCurrentMoveIdx('-1');
    setGameMetadata({
      visibility: 'Public',
      isPrivate: false,
      gameMode: null // Reset gameMode
    });
    setStarredMoves([]);
    setMoveContent('');
    setVariationContent('');
    setTags([]);
    setGame(new Chess());
    setRootFen(CHESSBOARD.START_POSITION);
    setOrientation('white');
    setStep(0);
    setSkipMoveIndexUpdate(false); // Make sure to reset this flag
    setOpenModalType(''); // Ensure modal is closed
    // +++ Reset puzzle mode context +++
    setIsPuzzleMode(false);
  };

  const onRootFenChange = (newFen) => {
    newFen = newFen || undefined;
    setRootFen(newFen);
    setPgn('');

    try {
      const game = new Chess(newFen);
      setGame(game);
      setGameMetadata({
        ...gameMetadata,
        fen: newFen,
      });
      setOrientation(getTurnInFen(newFen) === 'w' ? 'white' : 'black');

      // Reset everything else
      setGameCaption('');
      setPgn('');
      setMoves([]);
      setCurrentMoveIdx('-1');
    } catch (err) {

    }
  };

  // First, let's add a state for tracking game result information
  // Add this near the other state variables
  const [gameResultInfo, setGameResultInfo] = useState({
    result: null, // "draw", "checkmate", "normal", etc.
    winner: null, // "white", "black"
    reason: null  // Specific reason like "stalemate", "insufficient material", etc.
  });

  // Now modify the handlePgnParse function to handle result notations
  const handlePgnParse = (pgn) => {
    try {
      // Create a fresh Chess instance to ensure we're starting from scratch
      const newGame = new Chess();

      if (pgn.trim() !== '') {


        // First, extract any game result notation from the end of the PGN
        let cleanedPgn = pgn.trim();
        let gameResult = {
          result: 'normal',
          winner: null,
          reason: null
        };

        // Check for standard result notations at the end of the PGN
        if (cleanedPgn.match(/\s+1-0\s*$/)) {

          gameResult = {
            result: 'normal',
            winner: 'white',
            reason: 'resignation'
          };
          // Remove the result notation
          cleanedPgn = cleanedPgn.replace(/\s+1-0\s*$/, '');
        }
        else if (cleanedPgn.match(/\s+0-1\s*$/)) {

          gameResult = {
            result: 'normal',
            winner: 'black',
            reason: 'resignation'
          };
          // Remove the result notation
          cleanedPgn = cleanedPgn.replace(/\s+0-1\s*$/, '');
        }
        else if (cleanedPgn.match(/\s+1\/2-1\/2\s*$/)) {

          gameResult = {
            result: 'draw',
            winner: null,
            reason: 'agreement'
          };
          // Remove the result notation
          cleanedPgn = cleanedPgn.replace(/\s+1\/2-1\/2\s*$/, '');
        }
        else if (cleanedPgn.match(/\s+\*\s*$/)) {

          // Remove the result notation
          cleanedPgn = cleanedPgn.replace(/\s+\*\s*$/, '');
        }


        // Split the PGN into moves
        const moves = cleanedPgn.split(/\d+\./).filter((move) => move.trim() !== '');

        // Apply each move to the fresh game instance
        let moveApplied = false;
        moves.forEach((move) => {
          // Split by whitespace to get individual moves
          const moveParts = move.trim().split(/\s+/);

          // Process each potential move
          for (const potentialMove of moveParts) {
            // Skip empty strings or non-move notations
            if (!potentialMove || potentialMove.match(/^(1-0|0-1|1\/2-1\/2|\*)$/)) {
              continue;
            }

            try {
              // Attempt to apply the move

              if (newGame.move(potentialMove, { sloppy: true })) {
                moveApplied = true;


                // Check if this move is a checkmate
                if (potentialMove.includes('#')) {
                  gameResult.result = 'checkmate';
                  gameResult.winner = newGame.turn() === 'w' ? 'black' : 'white'; // Opposite of whose turn it is
                  gameResult.reason = 'checkmate';

                }
              }
            } catch (moveError) {
              console.warn(`[AddGame] Failed to apply move: ${potentialMove}`, moveError);
              // Continue to next move even if this one fails
            }
          }
        });

        // Only update game state if at least one move was applied
        if (moveApplied) {
          // Update game with the new instance
          setGame(newGame);
          setCurrentMoveIdx(`${newGame.history().length - 1}`);

          // Create entirely new moves array
          const newMoves = newGame
            .history()
            .map((move) => ({ notation: move, content: '', arrows: [] }));

          setMoves(newMoves);

          // Save the game result info for use in the Moves component
          setGameResultInfo(gameResult);


          // Always set the root FEN to the standard starting position for PGN
          setRootFen('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1');
        } else {
          console.error('[AddGame] No valid moves could be applied from the PGN');
        }
      } else {
        throw new Error('PGN is empty');
      }
    } catch (error) {
      console.error('Error parsing PGN:', error);
    }
  };

  const onStarMoveClicked = () => {
    if (currentMoveIdx === '-1') return;
    if (starredMoves.includes(currentMoveIdx)) {
      setStarredMoves((moves) => moves.filter((move) => move !== currentMoveIdx));
    } else {
      setStarredMoves((moves) => [...moves, currentMoveIdx]);
    }
  };

  const handlePremiumClick = () => {
    window.open('/premium', '_blank');
    // The current page/modal will remain unchanged
  };

  useEffect(() => {
    // Check if there's imported PGN data from PlayChess
    const importedPGN = localStorage.getItem('analysisImportPGN');
    if (importedPGN) {
      // Handle the imported PGN
      handlePgnParse(importedPGN);

      // Clear the localStorage after importing
      localStorage.removeItem('analysisImportPGN');
    }
  }, []);

  // Add function to handle difficulty change
  const handleDifficultyChange = (value) => {
    setSelectedDifficulty(value);
    localStorage.setItem('preferredDifficulty', value);
  };

  // Track the temporary new FEN position when closing FENBuilder
  const [pendingFen, setPendingFen] = useState(null);
  const [pendingPgn, setPendingPgn] = useState(null);

  // Track whether to skip the GameModeChooser modal
  const [skipGameModeChooser, setSkipGameModeChooser] = useState(false);

  // Effect to handle showing GameModeChooser after FENBuilder is closed
  useEffect(() => {
    // If FENBuilder just closed and we have pending data
    if (openModalType === '' && (pendingFen || pendingPgn)) {


      // IMPORTANT: Reset all board state completely before loading new position
      // This ensures we're not building on top of previous board state


      // Clear all game-related state
      setStarredMoves([]);
      setCurrentMoveIdx('-1');
      setMoveContent('');
      setVariationContent('');
      if (pendingFen) {

        // Important: Clear moves before setting FEN
        setMoves([]);
        onRootFenChange(pendingFen);
        setPendingFen(null); // Clear pending data
      }

      if (pendingPgn) {

        // For PGN, we need to completely reset the game first
        // Create a brand new Chess instance
        setGame(new Chess());
        // Reset the root FEN to the standard starting position
        setRootFen(CHESSBOARD.START_POSITION);
        // Clear existing moves before parsing PGN
        setMoves([]);
        // Now process the PGN with a clean slate
        handlePgnParse(pendingPgn);
        setPendingPgn(null); // Clear pending data
      }

      // Only show GameModeChooser if we're not skipping it (for PGN uploads)
      if (skipGameModeChooser) {

        setSkipGameModeChooser(false); // Reset for next time
      } else {
        setOpenModalType('GameModeChooser');
      }
    }
  }, [openModalType, pendingFen, pendingPgn, skipGameModeChooser]);

  // Add a useEffect to track modal changes
  useEffect(() => {
  }, [openModalType]);

  // Add effect to set up the control panel tooltips styling
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id = 'engine-panel-tooltip-styles-addgame';
    styleEl.textContent = `
      .engine-panel-btn .engine-panel-tooltip {
        visibility: hidden;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: -30px;
        background-color: rgba(0, 0, 0, 0.8);
        color: gold;
        text-align: center;
        border-radius: 4px;
        padding: 5px 8px;
        white-space: nowrap;
        z-index: 200;
        font-size: 12px;
        opacity: 0;
        transition: opacity 0.3s;
      }
      
      .engine-panel-btn:hover .engine-panel-tooltip {
        visibility: visible;
        opacity: 1;
      }
    `;
    document.head.appendChild(styleEl);

    return () => {
      if (document.getElementById('engine-panel-tooltip-styles-addgame')) {
        document.head.removeChild(styleEl);
      }
    };
  }, []);

  // Add effect to update boardStartingTurn when rootFen changes
  useEffect(() => {
    try {
      // Extract turn from FEN
      const fenTurn = getTurnInFen(rootFen);
      setBoardStartingTurn(fenTurn || 'w');
    } catch (error) {
      console.error('Error parsing FEN for turn:', error);
      setBoardStartingTurn('w'); // Default to white if there's an error
    }
  }, [rootFen]);

  // +++ Add effect to handle full game analysis completion +++
  useEffect(() => {
    window.onFullAnalysisComplete = (updatedMoves) => {
      
      // Update the state with the analysis results from Stockfish.js
      setMoves(updatedMoves);
    };

    // Cleanup function to remove the global callback when the component unmounts
    return () => {
      delete window.onFullAnalysisComplete;
    };
  }, [setMoves]); // Add setMoves as a dependency

  // Add state for current FEN position
  const [currentFen, setCurrentFen] = useState('');
  const [openingInfo, setOpeningInfo] = useState(null);
  // Reference for tracking API call timeout
  const fetchTimeoutRef = useRef(null);
  // Track last 20 fetched FENs to prevent duplicate requests
  const lastFetchedFens = useRef([]);
  
  
  
  // Function to fetch opening information from Firebase Cloud Function
  const getOpeningFromFEN = async (fen) => {
    try {
      const res = await fetch("https://us-central1-chessisfun-66b29.cloudfunctions.net/getOpening", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fen }),
      });
      return await res.json();
    } catch (error) {
      console.error("Error fetching opening information:", error);
      return null;
    }
  };

  // Handle FEN updates from the Board component with optimizations
  const handleFenChange = (fen) => {
    if (fen && fen !== currentFen) {
      setCurrentFen(fen);
      
      
      // Clear any pending timeout to implement debouncing
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      
      // Start a new debounced request
      fetchTimeoutRef.current = setTimeout(() => {
        // Get current move index (numeric value)
        const currentMoveNumeric = parseInt(currentMoveIdx);
        
        // Skip opening lookups after move 12
        if (currentMoveNumeric > 12) {
          
          return;
        }
        
        // Check if the current move already has opening data
        const currentMoveData = getMove(moves, currentMoveIdx);
        if (currentMoveData?.opening) {
          
          setOpeningInfo(currentMoveData.opening);
          return;
        }
        
        // Check if we've already fetched this FEN recently (avoid duplicate requests)
        if (lastFetchedFens.current.includes(fen)) {
          
          return;
        }
        
        // Add this FEN to our tracking array (keep only the last 12)
        lastFetchedFens.current.push(fen);
        if (lastFetchedFens.current.length > 12) {
          lastFetchedFens.current.shift(); // Remove oldest FEN
        }
        
        // Log the API request
        
        
        // Call the Cloud Function to get opening information
        getOpeningFromFEN(fen).then(opening => {
          if (opening && (opening.name || opening.eco)) {
            setOpeningInfo(opening);
            
            
            // Cache the opening data in the moves array
            const updatedMoves = [...moves];
            const currentMove = getMove(updatedMoves, currentMoveIdx);
            
            if (currentMove) {
              // Add opening data to the current move
              currentMove.opening = opening;
              setMoves(updatedMoves);
              
              // This will ensure the data is saved to the database when the game is saved
              
            }
          }
        });
      }, 500); // 500ms debounce delay
    }
  };
  
  
  
  if (loading) {
    return <ScreamSkeleton />;
  }

  const currentMove = getMove(moves, currentMoveIdx);
  const isLesson = () => {
    return tags.some(tag => tag.value === "Lesson");
  };

  // Add function to update variation content
  const updateVariationContent = (newContent) => {
    if (!isMoveInVariation(currentMoveIdx, moves)) {
      return;
    }

    const [moveRef, variationIdx] = getMoveAndVariationIndices(currentMoveIdx, moves);
    
    // Create a new moves array with the updated variation caption
    const updatedMoves = moves.map(move => {
      if (move === moveRef) {
        return {
          ...move,
          variationCaptions: {
            ...(move.variationCaptions || {}),
            [variationIdx]: newContent
          }
        };
      }
      return move;
    });

    setMoves(updatedMoves);
  };

  // Function to fetch opening data for the first 12 moves of the game
  const fetchOpeningsForGame = async () => {
    setIsLoadingOpenings(true);
    
    try {
      // Get FENs for the first 12 moves
      const fenPositions = [];
      
      // Add the starting position (root FEN)
      fenPositions.push(rootFen);
      
      // We need to create a temporary game instance to play through the moves and capture FENs
      const tempGame = new Chess(rootFen);
      
      // Get moves history from the current game instance
      const moveHistory = game.history({ verbose: true });
      
      
      
      // Get FENs for up to 12 moves by playing each move and recording position
      for (let i = 0; i < Math.min(12, moveHistory.length); i++) {
        try {
          // Apply the move to our temporary game
          tempGame.move(moveHistory[i]);
          // Record the position after this move
          fenPositions.push(tempGame.fen());
        } catch (err) {
          console.error("Error applying move for FEN extraction:", err);
        }
      }
      
      
      
      // Skip if we don't have any positions
      if (fenPositions.length === 0) {
        setIsLoadingOpenings(false);
        setOpeningOptions([{
          value: 'no-opening',
          label: 'Continue without opening',
          className: 'tag-openings',
          color: TAGS.Openings.color,
          background: TAGS.Openings.background
        }]);
        return;
      }
      
      // Fetch opening data for each position
      const openingPromises = fenPositions.map(fen => getOpeningFromFEN(fen));
      const openingResults = await Promise.all(openingPromises);
      
      
      
      // Filter results to only include valid openings
      const validOpenings = openingResults
        .filter(opening => opening && opening.eco) // Only keep results with ECO codes
        .filter(opening => {
          // Check if this ECO code is in our allowedEcoGroups
          for (const [groupName, ecoCodes] of Object.entries(allowedEcoGroups)) {
            if (ecoCodes.includes(opening.eco)) {
              // Add the group name to the opening
              opening.groupName = groupName;
              return true;
            }
          }
          return false;
        });
      
      
      
      
      // Extract unique opening groups
      const uniqueGroups = Array.from(new Set(validOpenings.map(o => o.groupName)));
      
      
      
      // Convert to dropdown options
      const options = uniqueGroups.map(groupName => ({
        value: groupName,
        label: groupName,
        className: 'tag-openings',
        width: '100%',
        color: TAGS.Openings.color,
        background: TAGS.Openings.background
      }));
      
      // // Add back option
      // options.unshift({
      //   value: 'back',
      //   label: '← Back to tags',
      //   className: 'tag-openings',
      //   color: 'white',
      //   background: '#333333'
      // });
      
      // Always add a "Continue without opening" option
      options.push({
        value: 'no-opening',
        label: 'Continue without opening',
        className: 'tag-openings',
        color: 'red',
        background: 'TAGS.Openings.background'
      });
      
      
      
      setOpeningOptions(options);
    } catch (error) {
      console.error("Error fetching openings:", error);
      // Provide a fallback option
      setOpeningOptions([{
        value: 'back',
        label: '← Back to tags',
        className: 'tag-openings',
        color: 'white',
        background: '#333333'
      }, {
        value: 'no-opening',
        label: 'Continue without opening',
        className: 'tag-openings',
        color: TAGS.Openings.color,
        background: TAGS.Openings.background
      }]);
    } finally {
      setIsLoadingOpenings(false);
    }
  };
  
  // Handle opening selection
  const handleOpeningSelect = (opening) => {
    if (!opening) return;
    
    if (opening.value === 'back') {
      // Return to tag selection mode
      setIsOpeningSelectionMode(false);
      return;
    }
    
    if (opening.value === 'no-opening') {
      // Just keep the Openings tag and exit opening selection mode
      setIsOpeningSelectionMode(false);
      return;
    }
    
    // Add both the general "Openings" tag and the specific opening tag
    const openingTagValue = `Openings - ${opening.label}`;
    const openingTag = {
      value: openingTagValue,
      label: openingTagValue,
      className: 'tag-openings',
      color: TAGS.Openings.color,
      background: TAGS.Openings.background
    };
    
    // Check if we already have the "Openings" tag
    const hasOpeningsTag = tags.some(tag => tag.value === 'Openings');
    
    if (hasOpeningsTag) {
      // Replace the existing Openings tag with the specific one
      const newTags = tags.filter(tag => tag.value !== 'Openings');
      setTags([...newTags, openingTag]);
    } else {
      // Add the specific opening tag
      setTags([...tags, openingTag]);
    }
    
    // Exit opening selection mode
    setIsOpeningSelectionMode(false);
  };
  
  // Handle back button in opening selection mode
  const handleOpeningSelectionBack = () => {
    setIsOpeningSelectionMode(false);
  };

  return (
    <div className="app-container">
      {openModalType === 'FAQAndSettings' && (
        <FAQAndSettingsModal onClose={() => setOpenModalType('')} />
      )}

      {openModalType === 'FENBuilder' && (
        <FENBuilderModal
          fen={game.fen()}
          onFENUpdate={(fen, gameResult) => {

            // Store FEN for processing after modal closes
            setPendingFen(fen);
          }}
          onPGNUpdate={(pgn, options) => {

            // Store PGN for processing after modal closes
            setPendingPgn(pgn);
          }}
          onClose={(params) => {


            // Check if we should skip the GameModeChooser (for PGN uploads)
            if (params?.skipGameModeChooser) {

              setSkipGameModeChooser(true);
            }

            setOpenModalType('');
          }}
        />
      )}
      {/*openModalType === 'Drafts' && (
        <DraftsModal
          open={true}
          onClose={() => setOpenModalType('')}
          primaryAction={() =>
            setGameMetadata((metadata) =>
              setGameMetadata({ ...gameMetadata, visibility: 'Drafts' }),
            )
          }
        />
      )}*/}
      {openModalType === 'GameModeChooser' && (
        <GameModeChooserModal
          onGameModeUpdate={(mode) => {

            setGameMetadata((gameMetadata) => ({
              ...gameMetadata,
              gameMode: mode,
            }));
            // Only add tags for specific modes with complete styling
            if (mode === "Puzzle") {
              setTags((tags) => [...tags, {
                value: "Puzzle",
                label: TAGS.Puzzle.label,
                className: TAGS.Puzzle.className,
                color: TAGS.Puzzle.color,
                background: TAGS.Puzzle.background
              }]);
              // +++ Set puzzle mode in context +++
              setIsPuzzleMode(true);
            } else if (mode === "Challenge") {
              setTags((tags) => [...tags, {
                value: "Challenge",
                label: TAGS.Challenge.label,
                className: TAGS.Challenge.className,
                color: TAGS.Challenge.color,
                background: TAGS.Challenge.background
              }]);
              // For Challenge mode, automatically switch to the Save & Share tab
              setStep(1);
            } else if (mode === "Lesson") {
              setTags((tags) => [...tags, {
                value: "Lesson",
                label: TAGS.Lesson.label,
                className: TAGS.Lesson.className,
                color: TAGS.Lesson.color,
                background: TAGS.Lesson.background
              }]);
            }
            // Close the controls panel when position is loaded
            setShowControls(false);
            // Close the modal

            setOpenModalType('');
          }}
          onClose={() => {

            setOpenModalType('');
          }}
        />
      )}
      {openModalType === 'DeleteConfirm' && (
        <DeleteConfirmModal
          onConfirm={() => {
            resetPost();
            setOpenModalType('');
          }}
          onClose={() => setOpenModalType('')}
        />
      )}
      {openModalType === 'StartPosition' && (
        <StartPositionModal
          currentFen={localStorage.getItem('currentPositionFen') || game.fen()}
          currentMoveIdx={currentMoveIdx}
          onPositionUpdate={(fen, options) => {
            if (options.resetMoves) {
              // Reset all moves and set the current position as start


              // First reset everything
              resetPost();

              // Then set the new FEN
              onRootFenChange(fen);

              // Use setTimeout to ensure this runs after state updates
              setTimeout(() => {
                setOpenModalType('GameModeChooser');
              }, 50);
            } else {
              // Keep trailing moves - implementation


              // Get the current move index as a number
              const currentIdx = parseInt(options.currentMoveIdx);

              if (currentIdx >= 0 && currentIdx < moves.length) {
                try {
                  // First, let's completely reset the state before building it back up
                  resetPost();

                  // Start with a clean chess instance using the current FEN
                  const newGame = new Chess(fen);

                  // Get trailing moves
                  const trailingMoves = moves.slice(currentIdx + 1);


                  // Now we need to properly transform these moves to work in the new context
                  // We'll create a temporary game to validate and transform these moves
                  const tempGame = new Chess(fen);
                  const transformedMoves = [];

                  // Create a separate game instance to track the original moves for detailed info
                  const originalGame = new Chess(rootFen);
                  // Play moves up to current index in original game to get to the same point
                  for (let i = 0; i <= currentIdx; i++) {
                    if (i < moves.length) {
                      try {
                        originalGame.move(moves[i].notation);
                      } catch (e) {
                        console.error(`Error replaying original move ${i}:`, e);
                      }
                    }
                  }



                  // Try to apply trailing moves to temp game and get proper notations
                  let allMovesApplied = true;
                  for (let i = 0; i < trailingMoves.length; i++) {
                    try {
                      const originalMove = trailingMoves[i];
                      const originalIndex = currentIdx + 1 + i;

                      // Try several methods to make the move successfully
                      let appliedMove = null;

                      // Method 1: Try using the original game's verbose move history
                      if (originalIndex < game.history({ verbose: true }).length) {
                        const verboseMove = game.history({ verbose: true })[originalIndex];
                        if (verboseMove) {
                          try {
                            appliedMove = tempGame.move({
                              from: verboseMove.from,
                              to: verboseMove.to,
                              promotion: verboseMove.promotion || undefined
                            });

                          } catch (e) {
                            console.warn(`Failed with from/to for move ${i}:`, e);
                          }
                        }
                      }

                      // Method 2: Try direct notation as fallback
                      if (!appliedMove) {
                        try {
                          appliedMove = tempGame.move(originalMove.notation);

                        } catch (e) {
                          console.warn(`Failed with notation for move ${i}:`, e);
                        }
                      }

                      if (appliedMove) {
                        // Successfully applied the move, add it to our transformed list
                        transformedMoves.push({
                          notation: appliedMove.san,  // Use the SAN from the applied move
                          content: originalMove.content || '',
                          arrows: originalMove.arrows || []
                        });
                      } else {
                        console.error(`Could not apply move ${i}. Stopping sequence.`);
                        allMovesApplied = false;
                        break;
                      }
                    } catch (err) {
                      console.error(`Error processing move ${i}:`, err);
                      allMovesApplied = false;
                      break;
                    }
                  }



                  if (transformedMoves.length > 0) {
                    // Set a flag to skip the automatic move application in useMoveIndex
                    setSkipMoveIndexUpdate(true);

                    // Set the root FEN to the new starting position
                    setRootFen(fen);

                    // Update game state with the new transformed moves
                    setGame(newGame);
                    setMoves(transformedMoves);
                    setCurrentMoveIdx('-1');

                    // Adjust starred moves indexes
                    const adjustedStarredMoves = starredMoves
                      .filter(idx => parseInt(idx) > currentIdx && parseInt(idx) - (currentIdx + 1) < transformedMoves.length)
                      .map(idx => (parseInt(idx) - (currentIdx + 1)).toString());

                    setStarredMoves(adjustedStarredMoves);




                    // Reset the skip flag after a timeout to allow state updates to complete
                    setTimeout(() => {
                      setSkipMoveIndexUpdate(false);
                    }, 300);

                    // Show game mode chooser dialog
                    setOpenModalType('GameModeChooser');
                  } else {
                    // If we couldn't apply any moves, just reset and use the FEN
                    resetPost();
                    onRootFenChange(fen);
                    setOpenModalType('GameModeChooser');
                  }
                } catch (err) {
                  console.error("Error processing trailing moves:", err);
                  // If there's an error, fallback to reset
                  resetPost();
                  onRootFenChange(fen);
                  setOpenModalType('GameModeChooser');
                }
              } else {
                // Invalid move index, just reset and use the FEN
                resetPost();
                onRootFenChange(fen);
                setOpenModalType('GameModeChooser');
              }
            }

            // Clean up local storage
            localStorage.removeItem('currentPositionFen');
          }}
          onClose={() => {
            setOpenModalType('');
            // Clean up local storage
            localStorage.removeItem('currentPositionFen');
          }}
        />
      )}
      <div className="column" style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'start',
        overflow: 'hidden',
        gap: '5px',
        width: 'auto',
        padding: '0px',
      }}><ChessAnalysisWrapper>
          <div style={{
            width: '100%',
            borderRadius: '10px',
            padding: '0px',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '20px',

            }}>
              <div style={{
                display: 'flex',
                gap: '10px',
              }}>
                <div style={{
                  height: `${boardSize + 189}px`,
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '10px',
                }}>
                  {localShowStockfishEval && (
                    <StockfishEval
                      evaluation={evaluation}
                      orientation={orientation}
                      height="100%"
                      width={30}
                      boardSize={boardSize}
                    />
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', }}>

                  <div ref={containerRef}
                    className="main-top-section"
                    style={{
                      width: boardSize + 10,
                      height: boardSize + 10,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      marginBottom: '10px',
                      transition: 'width 0.2s, height 0.2s',
                      padding: 0,
                      borderRadius: '5px',
                    }}>
                    {/* Display opening information above the board */}
                    {openingInfo && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '-40px',
                          left: '0',
                          width: '100%',
                          textAlign: 'center',
                          padding: '5px',
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                          color: 'gold',
                          borderRadius: '5px',
                          fontSize: '14px',
                          fontStyle: 'italic',
                          zIndex: 10,
                          color: 'gold',
                        }}
                      >
                        {openingInfo.name && (
                          <span>{openingInfo.name}</span>
                        )}
                        {openingInfo.eco && (
                          <span style={{ marginLeft: '8px', fontSize: '14px', opacity: 0.9 }}>ECO: {openingInfo.eco}</span>
                        )}
                      </div>
                    )}
                    <div style={{
                      width: boardSize,
                      height: boardSize,
                      boxSizing: 'border-box', boxShadow: '0 0 20px 5px rgba(0, 0, 0, 0.7)',
                      borderRadius: '5px',
                    }}>
                      {openModalType === 'FENBuilder' ? (
                        <div style={{
                          width: boardSize,
                          height: boardSize,
                          background: 'gray',
                        }}></div>
                      ) : (

                        <Board
                          game={game}
                          orientation={orientation}
                          moves={moves}
                          setMoves={setMoves}
                          currentMove={currentMoveIdx}
                          setCurrentMove={setCurrentMoveIdx}
                          isCommenting={false}
                          boardSize={boardSize}
                          boardTheme={localStorage.getItem('boardTheme') || 'blue'}
                          isDraggingDisabled={isChallenge()}
                          gameResultInfo={gameResultInfo}
                          onFenChange={handleFenChange}
                        />

                      )}
                    </div>
                  </div>

                  <div style={{
                    marginTop: '0',
                    width: boardSize + 10,
                    position: 'relative',
                    height: '150px', // Fixed height for component
                  }}>
                    {/* Always render Stockfish but control visibility with CSS */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 1,
                      visibility: localShowStockfish ? 'visible' : 'hidden',
                    }}>
                      <Stockfish isViewGame={false} />
                    </div>

                    {/* Show ENGINE OFF message when UI is hidden, but engine still runs */}
                    {!localShowStockfish && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          background: 'black',
                          borderRadius: '5px',
                          color: 'gold',
                          width: '100%',
                          height: '150px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          fontStyle: 'italic',

                          zIndex: 2,
                        }}
                      >
                        <div style={{ fontSize: '24px', fontWeight: 'bold' }}>Stockfish Engine Best Moves Hidden</div>
                        <div style={{ fontSize: '20px', marginTop: '5px', opacity: 0.8, cursor: 'pointer' }} onClick={handleStockfishToggle}>
                          Click to Reveal...
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </ChessAnalysisWrapper>
      </div>



      <div className="" style={{
        borderRadius: '5px',
        position: 'relative',
        margin: '10px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'  // Add this to prevent content overflow
      }}>
        <div className="tab-container">
          {step === 0 && (
            <>

              {/* First bordered group - Moves and Annotation */}
              <div style={{
                border: '5px solid #121212',
                borderRadius: '5px',
                padding: '15px',
                background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                height: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                margin: ''
              }}>
                {/* Second bordered group - Controls */}


                <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', }}>
                  {!showControls && moves?.length > 0 && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '10px 0px',
                      paddingBottom: '0px',
                      position: 'absolute',
                      bottom: '305px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 'calc(100% - 0px)',
                      zIndex: 10
                    }} className="render-Creator-div">
                      {/* Star icon and move notation content */}
                     
                      <span
                        style={{
                          height: '62px',
                          minWidth: '150px',
                          width: 'auto',
                          fontSize: '30px',
                          backgroundColor:
                            // Apply correct background color based on move color (white/black)
                            game?.history({ verbose: true })?.[0]?.color === 'w'
                              ? currentMove?.moveNum % 2 === 0
                                ? '#333333' // Black move (dark background)
                                : '#ffffff' // White move (light background)
                              : currentMove?.moveNum % 2 === 1
                                ? '#333333' // Black move (dark background) 
                                : '#ffffff', // White move (light background)
                          color:
                            // Apply correct text color based on background
                            game?.history({ verbose: true })?.[0]?.color === 'w'
                              ? currentMove?.moveNum % 2 === 0
                                ? '#f0f0f0' // White text on dark background
                                : '#222222' // Dark text on light background
                              : currentMove?.moveNum % 2 === 1
                                ? '#f0f0f0' // White text on dark background
                                : '#222222', // Dark text on light background
                          borderRadius: '5px',
                          marginRight: '10px',
                          border: 'none',
                          boxShadow: '0 0 12px rgba(88, 176, 71, 0.4)',
                          // Only add green border on bottom and right sides
                          borderBottom: '4px solid #58b047',
                          borderRight: '4px solid #58b047',
                          background: currentMove?.notation && starredMoves.includes(currentMoveIdx)
                            ? 'linear-gradient(315deg, black, gold)'  // Add gold gradient for starred moves
                            : game?.history({ verbose: true })?.[0]?.color === 'w'
                              ? currentMove?.moveNum % 2 === 0
                                ? 'linear-gradient(to bottom, #333333, #222222)' // Black move gradient
                                : 'linear-gradient(to bottom, #ffffff, #f0f0f0)' // White move gradient
                              : currentMove?.moveNum % 2 === 1
                                ? 'linear-gradient(to bottom, #333333, #222222)' // Black move gradient
                                : 'linear-gradient(to bottom, #ffffff, #f0f0f0)', // White move gradient
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                          animation: 'glow-current 2s infinite',
                        }}
                      >
                        {currentMove?.notation ? (
                          <span>
                            {Math.floor((parseInt(currentMoveIdx) + 2) / 2)}.
                            {parseInt(currentMoveIdx) % 2 === 1 ? '.. ' : ' '}
                            {currentMove.notation}
                          </span>
                        ) : (
                          <span className='move-new-game' style={{ background: 'linear-gradient(to bottom, #4ca140, #58b047)', topLeftRadius: '5px', topLeftBorderRadius: '5px' }}>Chess</span>
                        )}
                      </span>
                      

                      <div
                        onKeyDown={(e) => {
                          if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
                            // Stop event propagation completely
                            e.stopPropagation();
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                      >
                         <i
                        className='reg-buttons'
                        style={{
                          color: '#ffd700',
                          fontSize: '24px',
                          position: 'absolute',
                          bottom: '10px',
                          right: '7px',
                        }}
                        onClick={onStarMoveClicked}
                        class={clsx(
                          'fa-star',
                          starredMoves.includes(currentMoveIdx)
                            ? 'fa-solid'
                            : 'fa-regular',
                        )}
                      />
                        
                        <textarea
                          placeholder="Annotate this move..."
                          style={{
                            width: '350px',
                            height: '46px',
                            padding: '10px',
                            backgroundColor: '#222',
                            color: 'white',
                            border: '2px solid #57b047',
                            borderRadius: '5px',
                            fontSize: '18px',
                            resize: 'none',
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'gold #222',
                          }}
                          value={moveContent}
                          // onChange={(e) => setMoveContent(e.target.value)}
                          onChange={(e) => {
                            const newContent = e.target.value;
                            setMoveContent(newContent);
                            // Update the content in moves array immediately
                            updateContentForMove(newContent);
                          }}
                          onBlur={() => {
                            // updateContentForMove(moveContent);
                            setIsAnnotationFocused(false);
                          }}
                          onFocus={() => setIsAnnotationFocused(true)}
                          onKeyDown={(e) => {
                            // Directly stop propagation for arrow keys and spacebar
                            if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
                              // Allow the default behavior (cursor movement) but prevent the event from bubbling up
                              e.stopPropagation();

                            }
                          }}
                          
                        />
                      </div>
                    </div>
                  )}
                  {moves.length === 0 && !showControls && (<div style={{ width: '96%' }}><button
                    className="FEN-buttons reg-buttons green-hover advanced-setup-button"
                    onClick={() => setOpenModalType('FENBuilder')}
                    style={{
                      flex: '1',
                      fontSize: '35px',
                      border: '3px solid #57b047',
                      color: '#57b047',
                      width: '100%',
                      marginBottom: '15px',

                    }}
                  >
                    Advanced Setup&nbsp;&nbsp;<i class="fa-solid fa-file-import"></i>
                  </button></div>)}
                  {!showControls ? (
                    // Show the "OPEN CONTROLS" button when controls are hidden
                    <button
                      className="gray-hover-2"
                      onClick={() => setShowControls(true)}
                      style={{
                        width: '96%',
                        color: 'gray',
                        border: '3px solid gray',
                        padding: '10px 0px',
                        fontSize: '35px',
                        fontWeight: 'bold',
                        background: 'transparent',
                        height: '100px',
                        borderRadius: '5px',
                        position: 'relative',
                        marginBottom: '15px',

                        display: 'block',
                      }}
                    >

                      Control Panel&nbsp;&nbsp;<i className="fa-solid fa-gear"></i>
                    </button>
                  ) : (
                    // Show the controls section when showControls is true
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '96%',
                      marginBottom: '10px',
                    }}>
                      <div style={{
                        fontSize: '30px',
                        fontWeight: 'bold',
                        marginBottom: '5px',
                        textAlign: 'center',
                        borderBottom: '3px solid gray',
                        color: 'gray',
                        paddingBottom: '10px',
                        position: 'relative',
                        width: '100%',
                      }}>
                        C O N T R O L&nbsp;&nbsp;&nbsp;P A N E L
                        <i
                          className="fa-solid fa-circle-xmark"
                          style={{
                            position: 'absolute',
                            right: '25px',
                            top: '40%',
                            transform: 'translateY(-50%)',
                            fontSize: '30px',
                            color: '#ff0050',
                            cursor: 'pointer',
                            borderRadius: '5px',
                            padding: '5px',
                          }}
                          onClick={() => setShowControls(false)}
                        />
                      </div>

                      <div className="FEN-buttons-div" style={{
                        marginTop: '10px',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px'
                      }}>
                        <button
                          className={clsx(
                            "FEN-buttons",
                            "reg-buttons",
                            orientation === 'white' ? 'flip-button-white-oriented' : 'flip-button-black-oriented'
                          )}
                          style={{
                            flex: '1',
                            height: '70px',
                            fontSize: '20px',
                            // Remove inline background, border, color styles
                            // border: orientation === 'black' ? '3px solid white' : '3px solid black',
                            // background: orientation === 'black' ? 'black' : 'white',
                            // color: orientation === 'black' ? 'white' : 'black',
                          }}
                          onClick={() => setOrientation(getFlippedBoardOrientation(orientation))}
                        >
                          FLIP BOARD&nbsp;
                          <i className="fa-solid fa-retweet"></i>
                        </button>
                        <button
                          className="FEN-buttons reg-buttons white-hover"
                          style={{ flex: '1', height: '70px', fontSize: '20px', border: '3px solid gray' }}
                          onClick={() => setOpenModalType('FAQAndSettings')}
                        >
                          FAQs
                        </button>

                        <button
                          className="delete-button"
                          style={{ flex: '1', height: '70px', fontSize: '20px', border: '3px solid gray' }}
                          onClick={() => setOpenModalType('DeleteConfirm')}
                        >
                          START OVER&nbsp;&nbsp;<i className="fa-solid fa-trash"></i>
                        </button>
                      </div>

                      <div style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '8px',
                        marginTop: '15px',
                        width: '100%',
                        marginBottom: '5px',
                      }}>
                        <button
                          className="FEN-buttons reg-buttons green-hover"
                          onClick={() => setOpenModalType('FENBuilder')}
                          style={{
                            flex: '1',
                            border: '3px solid #57b047',
                            color: '#57b047',
                            height: '70px',
                            fontSize: '20px',
                          }}
                        >
                          ADVANCED SETUP
                        </button>
                        <button
                          className="FEN-buttons reg-buttons green-hover"
                          style={{
                            flex: '1',
                            border: '3px solid #57b047',
                            color: '#57b047',
                            height: '70px',
                            fontSize: '20px',
                          }}
                          onClick={() => {
                            // Get the FEN of the current position being viewed, not just the game state
                            let currentPositionFen;

                            if (currentMoveIdx === '-1') {
                              // If at the starting position, use the root FEN
                              currentPositionFen = rootFen;
                            } else {
                              // If viewing a move, get that move's resulting position
                              // We need to create a temporary game and replay moves up to the current index
                              const tempGame = new Chess(rootFen);
                              const moveHistory = game.history({ verbose: true });

                              // Replay moves up to the current index
                              for (let i = 0; i <= parseInt(currentMoveIdx); i++) {
                                if (i < moveHistory.length) {
                                  try {
                                    tempGame.move(moveHistory[i]);
                                  } catch (err) {
                                    console.error("Error replaying move:", err);
                                  }
                                }
                              }

                              currentPositionFen = tempGame.fen();
                            }


                            setOpenModalType('StartPosition');
                            // Store the current position FEN for the modal to use
                            localStorage.setItem('currentPositionFen', currentPositionFen);
                          }}
                        >
                          SET AS START POSITION
                        </button>
                      </div>
                    </div>
                  )}

                  {/* <div style={{
                    marginTop: showControls ? '10px' : '20px',
                    paddingBottom: '15px',
                    width: '96%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                    <Controls
                      game={game}
                      moves={moves}
                      setMoves={setMoves}
                      currentMove={currentMoveIdx}
                      setCurrentMove={setCurrentMoveIdx}
                      setPgn={setPgn}
                      showUndo
                      isAnnotationFocused={isAnnotationFocused}
                    />
                  </div> */}
                  <div className="" style={{ marginBottom: '5px', width: '96%', }}>
                    <button
                      className="next-prev-button gold-hover-2"
                      onClick={() => setStep((s) => (s ? 0 : 1))}
                      style={{
                        color: 'gold',
                        border: '3px solid gold',
                        borderRadius: '5px',
                        background: 'gold',
                        fontSize: '35px',
                        height: '100px',
                        zIndex: 999,
                        marginBottom: '5px',
                        width: '100%',
                        display: 'block',
                        background: 'transparent'
                      }}
                    >
                      Save & Share <i className="fa-solid fa-paper-plane"></i>
                    </button>
                  </div>
                </div>

                <div style={{
                  fontSize: '40px',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  textAlign: 'center',
                  borderBottom: '2px solid gold',
                  paddingBottom: '10px',
                  color: 'gold',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {/* +++ Conditionally render title +++ */}
                  {/* {gameMetadata?.gameMode === 'Puzzle' ? 'Puzzle Solution' : 'Game PGN'} */}
                  
                  {/* Display opening information next to the title */}
                  {openingInfo && openingInfo.name ? (
                   
                   <div className='opening-info' style={{fontSize: '20px', fontWeight: 'bold', color: 'gold'}}>{openingInfo.name}</div>
                   
                  ) : gameMetadata?.gameMode === 'Puzzle' ? 'Puzzle Solution' : 'Game PGN'}
                  {/* {!openingInfo && gameMetadata?.gameMode === 'Puzzle' ? 'Puzzle Solution' : 'Game PGN'} */}

                  {/* Add clickable chess-board icon */}
                  <i
                    ref={boardIconRef}
                    className="fa-solid fa-chess-board"
                    style={{
                      position: 'absolute',
                      left: '20px',
                      top: '50%',
                      transform: 'translateY(-65%)',
                      fontSize: '36px',
                      cursor: 'pointer',
                      // color: getRobotColor(),
                      color: showControlPanel ? '#00f2ea' : '#57b047',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowControlPanel(!showControlPanel);
                    }}
                    onMouseOver={(e) => {
                      e.target.style.color = '#00f2ea';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.color = showControlPanel ? '#00f2ea' : '#57b047';
                    }}
                  />

                  {/* Control panel for chess options */}
                  {showControlPanel && (
                    <div
                      ref={controlPanelRef}
                      style={{
                        position: 'absolute',
                        bottom: '5px',
                        left: '70px',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-around',
                        gap: '15px',
                        backgroundColor: 'rgba(0, 0, 0, 1)',

                        borderRadius: '8px',
                        border: '2px solid gold',
                        zIndex: 100,
                        boxShadow: '0 0 15px rgba(0, 0, 0, 0.8)',
                        alignItems: 'center',
                        height: '70px',
                        width: 'calc(100% - 150px)',
                      }}
                    >
                      {/* ENGINE Section */}

                      {/* Stockfish Icons toggle button */}
                      <div
                        className="engine-panel-btn"
                        style={{
                          position: 'relative',
                          display: 'inline-block'
                        }}
                      >
                        <div
                          onClick={handleStockfishIconsToggle}
                          style={{
                            cursor: 'pointer',
                            padding: '5px',
                            borderRadius: '50%',
                            boxShadow: localShowStockfishIcons
                              ? '0 0 8px rgba(76, 175, 80, 0.8)'
                              : '0 0 5px rgba(255, 215, 0, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: localShowStockfishIcons ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                            transition: 'all 0.3s ease',
                            height: '40px',
                            width: '40px',
                          }}
                        >
                          {localShowStockfishIcons ?
                            <img src={BrilliantGray} alt="Brilliant Icon" style={{ width: '30px', height: '30px' }} /> :
                            <img src={BrilliantBlue} alt="Brilliant Icon" style={{ width: '30px', height: '30px' }} />}
                        </div>
                        <div className="engine-panel-tooltip">
                          Move Classificaions {/*  {localShowStockfishIcons ? 'Off' : 'On'} */}
                        </div>
                      </div>


                      {/* Stockfish Engine */}
                      <div
                        className="engine-panel-btn"
                        style={{
                          position: 'relative',
                          display: 'inline-block'
                        }}
                      >
                        <i
                          className="fa-solid fa-robot"
                          style={{
                            cursor: 'pointer',
                            color: localShowStockfish ? 'rgb(76, 175, 80)' : '#f44336',
                            padding: '5px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: localShowStockfish ? '0 0 8px rgba(76, 175, 80, 0.8)' : '0 0 5px rgba(255, 215, 0, 0.3)',
                            background: localShowStockfish ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                            fontSize: '30px',
                            transition: 'all 0.3s ease',
                            height: '40px',
                            width: '40px',
                          }}
                          onClick={handleStockfishToggle}
                        ></i>
                        <div className="engine-panel-tooltip">
                          Stockfish Engine
                        </div>
                      </div>

                      {/* Stockfish Eval Bar */}
                      <div
                        className="engine-panel-btn"
                        style={{
                          position: 'relative',
                          display: 'inline-block'
                        }}
                      >
                        <i
                          className="fa-solid fa-chart-simple "
                          style={{
                            cursor: 'pointer',
                            color: localShowStockfishEval ? 'rgb(76, 175, 80)' : '#f44336',
                            padding: '5px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: localShowStockfishEval ? '0 0 8px rgba(76, 175, 80, 0.8)' : '0 0 5px rgba(255, 215, 0, 0.3)',
                            background: localShowStockfishEval ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                            fontSize: '30px',
                            transition: 'all 0.3s ease',
                            height: '40px', // Fixed height to match the Brilliant icon
                            width: '40px',  // Fixed width to match the Brilliant icon
                          }}
                          onClick={handleStockfishEvalToggle}
                        ></i>
                        <div className="engine-panel-tooltip">
                          Evaluation Bar
                        </div>
                      </div>




                      {/* Arrows toggle button - always on in AddGame */}
                      <div
                        className="engine-panel-btn"
                        style={{
                          position: 'relative',
                          display: 'inline-block'
                        }}
                      >
                        <i
                          className="fa-solid fa-arrow-up"
                          style={{
                            cursor: 'pointer',
                            color: 'rgb(76, 175, 80)', // Always green because always ON
                            padding: '5px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 0 8px rgba(76, 175, 80, 0.8)', // Always has glow because always ON
                            background: 'rgba(76, 175, 80, 0.1)',
                            fontSize: '30px',
                            transition: 'all 0.3s ease',
                            height: '40px',
                            width: '40px',
                          }}
                        ></i>
                        <div className="engine-panel-tooltip">
                          My Arrows
                        </div>
                      </div>


                    </div>

                  )}
                </div>

                {moves?.length === 0 && (
                  <div className="no-moves-container">
                    {gameMetadata.gameMode === "Puzzle" ? (
                      <div style={{ color: '#57b047', marginTop: '10px' }}>Create Your Puzzle Line,<br />Or Click On Stockfish.</div>
                    ) : gameMetadata.gameMode === "Lesson" || isLesson() ? (
                      <div style={{ color: '#57b047', marginTop: '10px' }}>Make The First Move<br />To Customize Your Lesson.</div>
                    ) : isChallenge() ? (
                      <div style={{ color: '#57b047', marginTop: '10px' }}>Click Save & Share<br />To Publish Your Challenge.</div>
                    ) : (
                      <div style={{ color: '#57b047', marginTop: '10px' }}>Make the first move,<br />@{props?.user?.credentials?.handle}.</div>
                    )}
                  </div>
                )}

                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  positition: 'absolute',
                  height: '75px',
                  width: '525px',
                  scrollbarWidth: 'none',
                  scrollbarColor: 'transparent transparent',
                }}>
                  <Moves
                    moves={moves}
                    starredMoves={starredMoves}
                    currentMove={currentMoveIdx}
                    // setCurrentMove={setCurrentMoveIdx}
                    setCurrentMove={handleMoveClick}
                    gameResultInfo={gameResultInfo}
                    currentTurn={game?.history({ verbose: true })?.[0]?.color}
                    className={""}
                    startingTurn={boardStartingTurn}
                  />

{/* Variation Caption - Causing Issues Of Duplication w/ Annotations */}
{/* {isMoveInVariation(currentMoveIdx, moves) && (
                          <textarea 
                            placeholder="Add variation caption here..."
                            style={{
                              width: '490px',
                              height: '28px',
                              marginLeft: '10px',
                              marginTop: '10px',
                              padding: '10px',
                              backgroundColor: '#222',
                              color: 'white',
                              border: '2px solid gold',
                              borderRadius: '5px',
                              fontSize: '18px',
                              resize: 'none',
                              scrollbarWidth: 'thin',
                              scrollbarColor: 'gold #222',
                            }}
                            value={variationContent}
                            // onChange={(e) => setVariationContent(e.target.value)}
                            // onBlur={() => {
                            //   const movesNew = JSON.parse(JSON.stringify(moves));
                            //   const [moveRef, variationIdx] = getMoveAndVariationIndices(currentMoveIdx, movesNew);

                            //   if (!moveRef["variationCaptions"]) {
                            //     moveRef["variationCaptions"] = {};
                            //   }
                            //   moveRef["variationCaptions"][variationIdx] = variationContent;
                            //   setMoves(movesNew);
                            //   setVariationContent('');
                            onChange={(e) => {
                              const newContent = e.target.value;
                              setVariationContent(newContent);
                              // Update the variation content in moves array immediately
                              updateVariationContent(newContent);
                            }}
                            onBlur={() => setIsAnnotationFocused(false)}
                            onFocus={() => setIsAnnotationFocused(true)}

                            onKeyDown={(e) => {
                              // Directly stop propagation for arrow keys and spacebar
                              if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
                                // Allow the default behavior (cursor movement) but prevent the event from bubbling up
                                e.stopPropagation();

                              }
                            }}
                          />
                        )} */}


                  {!showControls && moves?.length > 0 && (
                    <div style={{
                      marginTop: showControls ? '10px' : '20px',
                      paddingBottom: '15px',
                      width: '540px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'absolute',
                      // bottom: '310px',
                      bottom: '225px',
                      left: '12px',
                      zIndex: 10,
                      opacity: moves?.length > 0 ? 1 : showControls ? 1 : 0,
                    }} className=''>
                      <Controls
                        game={game}
                        moves={moves}
                        setMoves={setMoves}
                        currentMove={currentMoveIdx}
                        // setCurrentMove={setCurrentMoveIdx}
                        handleCurrentMoveUpdate={handleMoveClick}
                        setPgn={setPgn}
                        showUndo
                        isAnnotationFocused={isAnnotationFocused}
                      />
                    </div>
                  )}
                </div>



              </div>


            </>
          )}


          {step === 1 && (
            <>
              {/* First bordered group - Moves and Annotation */}
              <div style={{

                border: '5px solid #121212',
                borderRadius: '5px',
                padding: '15px',
                backgroundColor: 'black',
                height: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                margin: '',
                background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',

              }}
              >

                <div style={{ position: 'absolute', bottom: '0px', left: '0', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', }}>

                  <div className="tooltip-container" style={{ width: '96%', position: 'relative', marginBottom: '15px' }}>
                    <button
                      className="publish-buttons"
                      style={{
                        opacity: isFormValid() ? 1 : 0.5,
                        cursor: isFormValid() ? 'pointer' : 'not-allowed',
                        display: 'block',
                        height: '100px',
                        fontSize: '35px',
                        border: '5px solid #ff0050',
                        borderRadius: '5px',
                      }}
                      onClick={() => {
                        if (isFormValid()) {
                          onSubmitGame();
                        } else {
                          // Show validation message, although the Publish Button ususally prevents invalid submissions.
                          if (tags.length === 0) {
                            alert('Please add at least 1 tag');
                          } else if (tags.length > 2) {
                            alert('Maximum 2 tags allowed');
                          } else if (gameCaption.trim().length < 2) {
                            alert('Please add a caption (minimum 2 characters)');
                          } else if (!isChallenge() && moves.length === 0) {
                            // Only show this message for non-Challenge games
                            alert('Please add at least one move');
                          }
                        }
                      }}
                      disabled={!isFormValid()}
                    >
                      P U B L I S H&nbsp;&nbsp;&nbsp;&nbsp;G A M E
                    </button>
                    {!isFormValid() && (
                      <div className="tooltip publish-tooltip" style={{
                        textAlign: 'left',
                        padding: '5px 15px',
                        border: '3px solid #ff0050',
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bottom: '110px',
                        width: '500px',
                        fontSize: '20px',
                        zIndex: 1005,
                        backgroundColor: 'black',
                        borderRadius: '8px',
                        paddingLeft: '10px',
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)'
                      }}>
                        <strong>Requirements to Publish:</strong>
                        <ul style={{ marginTop: '5px', marginBottom: '0' }}>
                          <li>- Most Game Modes Require Moves To Be Made</li>

                          <li>- Add Title & 1-2 Tags</li>
                        </ul>
                      </div>
                    )}
                  </div>

                  <button
                    className="next-prev-button gold-hover-2"
                    onClick={() => setStep((s) => (s ? 0 : 1))}
                    style={{
                      color: 'gold',
                      border: '3px solid gold',
                      borderRadius: '5px',
                      background: 'transparent',
                      fontSize: '35px',
                      height: '100px',
                      marginTop: '',
                      marginBottom: '10px',
                      width: '96%',
                      display: 'block',
                      position: 'relative',
                    }}
                  >
                    Return To Game PGN <i className="fa-solid fa-chess-board"></i>
                  </button>

                </div>

                <div style={{
                  fontSize: '40px',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  textAlign: 'center',
                  borderBottom: '2px solid gold',
                  paddingBottom: '10px',
                  color: 'gold',
                }}>
                  Share Settings&nbsp;&nbsp;<i className="fa-solid fa-paper-plane"></i>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginLeft: '10px',
                    marginRight: '10px',
                    marginTop: '20px',
                    marginBottom: '10px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      width: '15%',
                      color: gameCaption.length >= 2 ? 'white' : '#ff0050',
                    }}
                  >
                    Title:
                  </span>
                  <input
                    className="caption-input placeholder-red"
                    style={{
                      marginTop: '',
                      color: 'white',
                      border: '1px solid white',
                      backgroundColor: 'black',
                      background: 'black',
                      width: '325px'
                    }}
                    value={gameCaption}
                    onChange={(e) => setGameCaption(e.target.value)}
                    placeholder="Minimum 2 Characters..."
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginLeft: '10px',
                    marginRight: '10px',
                    marginTop: '10px',
                  }}
                >

                  <span
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      width: '15%',
                      color: tags.length > 0 ? 'white' : '#ff0050',
                    }}
                  >
                    Tags:
                  </span>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '150px' }}>
                    <Select
                      className="tags-input"
                      options={isOpeningSelectionMode 
                        ? openingOptions 
                        : Object.entries(TAGS)
                        .filter(([key, { label, sisterGroup }]) => {
                          // Hide The-Pros tag for non-admin/editor users (uses same pattern as puzzle hiding)
                          const user = props.user?.credentials || {};
                          // If it's The-Pros tag and user either has no role OR is not Admin/Editor
                          if (key === 'The-Pros' && (!user.role || (user.role !== 'Admin' && user.role !== 'Editor'))) {
                            return false;
                          }

                          // Always show the currently selected tags
                          if (tags.some(tag => tag.value === key)) {
                            return true;
                          }

                          // Filter out Puzzle and Challenge tags if not already selected
                          // They can only be added through GameModeChooserModal
                          if (key === 'Puzzle' && !tags.some(tag => tag.value === 'Puzzle')) {
                            return false;
                          }

                          if (key === 'Challenge' && !tags.some(tag => tag.value === 'Challenge')) {
                            return false;
                          }

                          // if (key === 'Lesson' && !tags.some(tag => tag.value === 'Lesson')) {
                          //   return false;
                          // }

                          // If no tags selected yet, show all options EXCEPT Puzzle, Challenge, and Lesson
                          if (!tags.length) {
                            return key !== 'Puzzle' && key !== 'Challenge'; // && key !== 'Lesson'
                          }

                          // Filter out any tags that share a sister group with selected tags
                          const selectedSisterGroups = new Set(
                            tags.map(tag => TAGS[tag.value] ? TAGS[tag.value].sisterGroup : null)
                              .filter(group => group !== null)
                          );
                          return !selectedSisterGroups.has(sisterGroup);
                        })
                        .map(([key, { label, className, color, background }]) => ({
                          value: key,
                          label: label,
                          className: className,
                          color: color,
                          background: background
                        }))}
                      isMulti={!isOpeningSelectionMode}
                      onChange={(newSelection) => {
                        if (isOpeningSelectionMode) {
                          // Handle opening selection
                          handleOpeningSelect(newSelection);
                        } else {
                          // Handle regular tag selection
                          if (newSelection.length <= 2) {
                            // Check if "Openings" tag was just selected
                            const hasOpenings = newSelection.some(tag => tag.value === 'Openings');
                            const hadOpenings = tags.some(tag => tag.value === 'Openings');
                            
                            if (hasOpenings && !hadOpenings) {
                              // User just selected Openings tag
                              setTags(newSelection); // Update tags immediately
                              // Enter opening selection mode and fetch openings
                              setIsOpeningSelectionMode(true);
                              fetchOpeningsForGame();
                            } else {
                              // Normal tag update
                              setTags(newSelection);
                            }
                          } else {
                            // If trying to add a third tag, keep only the first two
                            setTags(newSelection.slice(0, 2));
                            // Show error indicator for 2 seconds
                            setShowTagError(true);
                            setTimeout(() => setShowTagError(false), 2000);
                          }
                        }
                      }}
                      value={isOpeningSelectionMode ? null : tags}
                      placeholder={isOpeningSelectionMode 
                        ? (isLoadingOpenings ? "Loading openings..." : "Select an opening") 
                        : "Min: 1, Max: 2"
                      }
                      isClearable={false} // Disable the parent clear X button
                      styles={{
                        placeholder: (baseStyles) => ({
                          ...baseStyles,
                          fontFamily: 'Poppins',
                          paddingLeft: '10px',
                          background: 'black',
                          color: '#ff0050' // Change placeholder to red
                        }),
                        control: (baseStyles, { isFocused }) => ({
                          ...baseStyles,
                          border: `1px solid ${showTagError ? '#ff0050' : 'white'}`, // Red border when error, otherwise white
                          backgroundColor: 'black', // Set background to black
                          boxShadow: 'none', // Remove the blue shadow on focus
                          '&:hover': {
                            border: `1px solid ${showTagError ? '#ff0050' : 'white'}` // Remove hover color change
                          },
                          minHeight: '48px', // Set fixed height
                          maxHeight: '48px', // Lock maximum height
                          width: '400px', // Fixed width
                          maxWidth: '400px' // Maximum width
                        }),
                        multiValue: (baseStyles, { data }) => ({
                          ...baseStyles,
                          background: data.background,
                          borderRadius: '5px'
                        }),
                        multiValueLabel: (baseStyles, { data }) => ({
                          ...baseStyles,
                          fontFamily: 'Poppins',
                          color: data.color,
                          background: data.background
                        }),
                        multiValueRemove: (baseStyles, { data }) => ({
                          ...baseStyles,
                          color: data.color,
                          background: data.background,
                          ':hover': {
                            backgroundColor: 'rgba(0,0,0,0.2)',
                            color: data.color
                          }
                        }),
                        input: (baseStyles) => ({
                          ...baseStyles,
                          width: 100,
                          height: 32,
                          maxHeight: 32,
                          display: 'flex',
                          alignItems: 'center',
                          color: 'white', // Set input text color to white
                          overflow: 'hidden' // Prevent overflow
                        }),
                        singleValue: (baseStyles) => ({
                          ...baseStyles,
                          color: 'white', // Change to white from black
                          marginTop: 2
                        }),
                        option: (baseStyles, { data }) => ({
                          ...baseStyles,
                          fontFamily: 'Poppins',
                          color: data.color,
                          background: data.background
                        }),
                        menu: (baseStyles) => ({
                          ...baseStyles,
                          backgroundColor: 'black', // Set dropdown menu background to black
                          width: '250px', // Match control width
                          maxWidth: '250px'
                        }),
                        valueContainer: (baseStyles) => ({
                          ...baseStyles,
                          maxHeight: '38px', // Match the control height
                          overflow: 'hidden' // Hide overflow content
                        }),
                        container: (baseStyles) => ({
                          ...baseStyles,
                          height: '38px', // Fixed container height
                          width: '250px', // Fixed width
                          maxWidth: '250px' // Maximum width
                        }),
                        dropdownIndicator: (baseStyles) => ({
                          ...baseStyles,
                          paddingRight: 20 // Move the dropdown arrow away from edge
                        })
                      }}
                    />
                  </div>
                </div>
                {/* <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      gap: '20px',
                      marginTop: '15px',
                      marginBottom: '20px',
                      marginLeft: '10px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        width: '',
                        color: 'white',
                      }}
                    >
                      Players:
                    </span>
                    <input
                      className="caption-input-half"
                      placeholder="White"
                    />

                    <input
                      className="caption-input-half"
                      placeholder="Black"
                    /><span style={{ fontSize: '16px', color: 'gray' }}>(optional)</span>
                  </div> */}



                {/* Add Difficulty selection dropdown */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '20px',
                    marginLeft: '10px',
                    marginRight: '10px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className="tooltip-container" style={{ position: 'relative' }}>
                      <i
                        className="fa-solid fa-info-circle"
                        style={{ cursor: 'pointer', color: 'gray', fontSize: '16px', marginRight: '10px' }}
                      ></i>
                      <span className="tooltip" style={{
                        textAlign: 'left',
                        fontSize: '18px',
                        paddingBottom: '',
                        display: 'flex',
                        flexWrap: 'wrap',
                        left: '265px',
                        top: '-20px',
                        width: '465px', /* Adjusted for better readability */
                        height: '40px', /* Let it adjust dynamically */
                        color: 'gold',
                        overflow: 'hidden',
                        zIndex: 99995,
                        lineHeight: '1.4',
                        backgroundColor: 'black',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'

                      }}>
                        Select the skill level for this content. This helps users find appropriate content for their level.
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        width: '',
                        color: 'white',
                      }}
                    >
                      Difficulty:
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <select
                      value={selectedDifficulty}
                      onChange={(e) => handleDifficultyChange(e.target.value)}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '5px',
                        border: '1px solid white',
                        backgroundColor: 'black',
                        color: 'white',
                        fontSize: '16px',
                        width: '250px',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      {Object.keys(DIFFICULTY).map((key) => (
                        <option key={key} value={key}>
                          {DIFFICULTY[key].label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Add Bookmark Folder selection */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '20px',
                    marginLeft: '10px',
                    marginRight: '10px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div className="tooltip-container" style={{ position: 'relative' }}>
                      <i
                        className="fa-solid fa-info-circle"
                        style={{ cursor: 'pointer', color: 'gray', fontSize: '16px', marginRight: '10px' }}
                      ></i>
                      <span className="tooltip" style={{
                        textAlign: 'left',
                        fontSize: '18px',
                        paddingBottom: '',
                        display: 'flex',
                        flexWrap: 'wrap',
                        left: '270px',
                        top: '-20px',
                        width: '465px', /* Adjusted for better readability */
                        height: '40px', /* Let it adjust dynamically */
                        zIndex: 999999,
                        lineHeight: '1.4',
                        color: 'gold',
                        background: 'black',
                        padding: '15px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal'

                      }}>
                        {isCreatingNewFolder
                          ? "After Publishing, a new folder will be created and this game will be added to it."
                          : "Optional: Save this game to a bookmark folder while publishing."
                        }
                      </span>
                    </div>
                    {!isCreatingNewFolder && (
                      <span
                        style={{
                          fontSize: '24px',
                          fontWeight: 'bold',
                          width: '',
                          color: 'white',
                        }}
                      >
                        Bookmark:
                      </span>
                    )}
                  </div>

                  {!isCreatingNewFolder ? (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <select
                        value={selectedBookmarkFolder}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === 'NEW_FOLDER') {
                            setIsCreatingNewFolder(true);
                            setNewFolderName('');
                            setNewFolderPrivate(false);

                          } else {
                            setSelectedBookmarkFolder(value);
                            setIsCreatingNewFolder(false);

                          }
                        }}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '5px',
                          border: '1px solid white',
                          backgroundColor: 'black',
                          color: 'white',
                          fontSize: '16px',
                          width: '250px',
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                      >
                        <option value="NONE">-- None --</option>
                        {userFolders.map((folder) => (
                          <option key={folder.name} value={folder.name}>
                            {folder.name} {folder.isPrivate ? '(Private)' : '(Public)'}
                          </option>
                        ))}
                        <option value="NEW_FOLDER">+ Create New Folder</option>
                      </select>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '10px' }}>
                      <div style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'center' }}>
                        <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', marginRight: '10px' }}>Name:</span>
                        <input
                          type="text"
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          placeholder="Enter new folder name"
                          style={{
                            padding: '10px',
                            borderRadius: '5px',
                            border: '1px solid white',
                            backgroundColor: 'black',
                            color: 'white',
                            fontSize: '16px',
                            flex: 0.93,
                            outline: 'none'
                          }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '' }}>
                          <input
                            type="checkbox"
                            checked={newFolderPrivate}
                            onChange={() => setNewFolderPrivate(!newFolderPrivate)}
                            id="new-folder-private"
                            style={{ marginRight: '8px', cursor: 'pointer' }}
                          />
                          <label
                            htmlFor="new-folder-private"
                            style={{ fontSize: '16px', color: 'white', cursor: 'pointer' }}
                          >
                            Private
                          </label>
                        </div>
                        <button
                          onClick={() => {
                            setIsCreatingNewFolder(false);
                            setSelectedBookmarkFolder('NONE');

                          }}
                          style={{
                            padding: '10px 15px',
                            borderRadius: '5px',
                            border: '1px solid #ff0050',
                            backgroundColor: 'transparent',
                            color: '#ff0050',
                            fontSize: '16px',
                            cursor: 'pointer'
                          }}
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{
                  color: 'gray',
                  fontSize: '16px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: '30px',
                  fontStyle: 'italic',
                  textAlign: 'center',

                }}>
                  All games are publicly visible, regardless of their folder privacy.<br /><br />Private games, and more features coming soon!


                </div>
                {/* <div
                    style={{
                      color: '',
                      fontWeight: '',
                      marginTop: '30px',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '10px',
                      marginLeft: '',
                    }}
                  >
                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'gold', marginRight: '10px' }}>
                      @{props?.user?.credentials?.handle}
                    </span>
                    {VISIBILITIES.map((v) => {
                      const isSelected = gameMetadata.visibility === v;
                      return (
                        <button
                          key={v}
                          style={{
                            color: 'white',
                            border: '3px solid white',
                            opacity: isSelected ? '1' : '0.4',
                          }}
                          className={clsx(
                            'visibility-button',
                            isSelected && 'selected'
                          )}
                          onClick={() => {
                            const isPrivate = v === 'Private';
                            
                            setGameMetadata({
                              ...gameMetadata,
                              visibility: v,
                              isPrivate: isPrivate
                            });
                          }}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div> */}



              </div>


            </>
          )}

        </div>
      </div>

      {/* Notification popup for bookmark operations */}
      {
        bookmarkNotification && (
          <div style={{
            position: "absolute",
            right: "20px",
            bottom: "20px",
            backgroundColor: bookmarkNotification.type === 'error' ? 'rgba(255, 0, 0, 0.8)' : 'rgba(0, 128, 0, 0.8)',
            color: 'white',
            padding: '10px 15px',
            borderRadius: '5px',
            zIndex: 1001,
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {bookmarkNotification.message}
          </div>
        )
      }
    </div >
  );
};

AddGame.propTypes = {
  getScreams: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  data: state.data,
  user: state.user,
});

export default connect(mapStateToProps, { getScreams })(AddGame);

